/**
 * <Heading> — canonical heading primitive.
 *
 * Emits a semantic <h1>...<h5> with the SMMS design-system typography
 * tokens locked by:
 *   - D.2 — page H1 = text-xl (20px) font-semibold text-text
 *   - Strategist A's cascading hierarchy (docs/design-system/02-typography.md)
 *
 * The header-consistency rule is enforced here: same `level` ALWAYS produces
 * the same visual treatment regardless of whether the heading sits inside a
 * card, drawer, modal, dashboard widget, or the page chrome. Drift cannot
 * recur as long as consumers go through this primitive.
 *
 * Per the locked decision, raw <h1>-<h5> outside src/design-system/ is
 * banned by ESLint (Phase 2 ships the rule). The 131 pseudo-headings
 * (<div className="text-2xl font-bold">) found in Phase-1 §3.6 are the
 * other migration target — codemod runs as part of Phase 13 page-by-page
 * cleanup.
 *
 * Escape hatches:
 *   - `weight` and `serif` exist for marketing-only emphasis (D.3).
 *     The ESLint rule that locks these is Phase 8 (style-only rule).
 *   - `as` lets you override the rendered tag for SEO/structure
 *     (e.g. dashboard widget headings rendered as <h2> structurally
 *     but visually `level={3}`). Use sparingly.
 *
 * Per D.4: dark-mode safe — `text-text` resolves via the
 * design-system/tokens/global.css custom property.
 */

import { forwardRef, type HTMLAttributes, type ElementType, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5;

export interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'children'> {
  /**
   * Logical hierarchy level. Drives the visual treatment AND the rendered
   * semantic tag (unless `as` is supplied).
   *
   *   1 — Page title. text-xl (20px) font-semibold (D.2).
   *   2 — Section title. text-lg (17px) font-semibold.
   *   3 — Card / widget / drawer / modal title. text-md (15px) font-semibold.
   *   4 — Sub-grouping inside cards or sections. text-sm (13px) font-semibold.
   *   5 — Eyebrow / group label. text-xs (11px) font-semibold uppercase tracking-wider.
   */
  level: HeadingLevel;

  /**
   * Render an alternate tag for document outline purposes while keeping the
   * level's visual treatment. Use sparingly — accessibility benefits from
   * the default mapping.
   */
  as?: ElementType;

  /**
   * Override font-weight. Default tracks the level (semibold for 1-4,
   * semibold-uppercase for 5). Marketing-only escape hatch.
   */
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';

  /**
   * Use the editorial display serif (Fraunces) instead of the default
   * sans (Inter Tight). Marketing-only escape hatch (D.3) — operational
   * pages use the default.
   */
  serif?: boolean;

  /**
   * Highlight the last word of a string title in `text-primary`. Used on
   * page H1s for the "Audit <span class=text-primary>Reports</span>" pattern.
   *
   * Behavior is intentionally conservative:
   *   - Only fires when `children` is a single string AND contains 2+ words.
   *   - Single-word strings render unchanged (no accent — looks identical).
   *   - Non-string children (icons + spans, fragments) render unchanged so
   *     consumers that already hand-roll their own accent (or render dynamic
   *     data like a work-order number / asset name) keep total control.
   *
   * Localization caveat: word-final accenting reads as language-aware emphasis
   * in English; other languages may put the conceptual head of the phrase
   * elsewhere. Revisit when i18n lands (post-Phase-14).
   */
  accent?: boolean;

  children: ReactNode;
}

const LEVEL_TAG: Record<HeadingLevel, ElementType> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
};

const LEVEL_CLASSES: Record<HeadingLevel, string> = {
  // D.2 — page title
  1: 'text-xl font-semibold leading-tight tracking-tight text-text',
  // Section title
  2: 'text-lg font-semibold leading-tight tracking-tight text-text',
  // Card / widget / drawer / modal title
  3: 'text-md font-semibold leading-tight tracking-tight text-text',
  // Sub-grouping
  4: 'text-sm font-semibold leading-snug text-text',
  // Eyebrow / group label
  5: 'text-xs font-semibold uppercase tracking-wider text-muted',
};

const WEIGHT_CLASSES: Record<NonNullable<HeadingProps['weight']>, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { level, as, weight, serif, accent, className, children, ...rest },
  ref,
) {
  const Tag = (as ?? LEVEL_TAG[level]) as ElementType;

  // Accent: split a string title on its final whitespace and color the trailing
  // word with `text-primary`. Conservative — only string children, only 2+ words.
  const renderedChildren = (() => {
    if (!accent || typeof children !== 'string') return children;
    const trimmed = children.trim();
    const lastSpace = trimmed.lastIndexOf(' ');
    if (lastSpace < 0) return children;
    const head = trimmed.slice(0, lastSpace);
    const tail = trimmed.slice(lastSpace + 1);
    return (
      <>
        {head}{' '}
        <span className="text-primary">{tail}</span>
      </>
    );
  })();

  return (
    <Tag
      ref={ref}
      className={cn(
        LEVEL_CLASSES[level],
        weight && WEIGHT_CLASSES[weight],
        serif && 'font-display',
        className,
      )}
      {...rest}
    >
      {renderedChildren}
    </Tag>
  );
});

Heading.displayName = 'Heading';

export default Heading;
